import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import {
    EMPTY_ARRAY_LENGTH,
    IAumMenuItem,
    IColumn,
    ITableOptions,
    LOCALE_LOCALIZATION_KEYS_MAP,
} from '@modeso/aumico-lib-core-fe';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import Debug from 'debug';
import { BehaviorSubject, Observable } from 'rxjs';

import { I18NextService } from 'angular-i18next';
import { AnnualReportMetaInformationDTO } from '../../dtos/annualReportMetaInformation.dto';
import { IDispatchAnnualReportsFilter } from '../../dtos/dispatch-annual-reports-filter.dto';
import { AnnualReportListMenuActions } from '../../enums/annualReportListMenuActions.enum';
import { AnnualReportListViewState } from '../../enums/annualReportListViewState.enum';
import { IAnnualReportTableList } from '../../interfaces/IAnnualReportTableList.interface';
import { AnnualReportProvider } from '../../provider/annualReport.provider';
import { localizationKeys } from './../../constants/localizationKeys';

const debug = Debug('aumico:aumico-lib-annualreport-fe:AnnualReportListPage');
@UntilDestroy()
@Component({
    selector: 'aumico-annual-report-list-page',
    templateUrl: 'annual-report-list.page.html',
    styleUrls: ['annual-report-list.page.scss'],
})
export class AnnualReportListPage implements OnInit {
    selectedIds: string[];
    menuOptions: IAumMenuItem[];
    @Input() public set injectedProviderSetter(injectedProvider: AnnualReportProvider) {
        if (injectedProvider) {
            this.provider = injectedProvider;
        }
    }
    @Input() userPreferredNumberOfDocumentsPerPage: number | undefined;

    private annualReportsFilter: IDispatchAnnualReportsFilter | undefined;
    @Input() set filter(value: IDispatchAnnualReportsFilter | undefined | null) {
        // undefined value refers to uncertainity yet if there is a filter or not. So we don't do an unnecessary dispatch
        if (value === undefined) {
            return;
        }

        this.annualReportsFilter = value;

        if (this.viewState === AnnualReportListViewState.ACTIVE) {
            if (value === null || value.companyName?.trim().length === EMPTY_ARRAY_LENGTH) {
                this.provider.dispatchActiveAnnualReportsList();
            } else {
                this.provider.dispatchActiveAnnualReportsList(value);
            }
        } else if (this.viewState === AnnualReportListViewState.ARCHIVED) {
            if (value === null || value.companyName?.trim().length === EMPTY_ARRAY_LENGTH) {
                this.provider.dispatchArchivedAnnualReportsList();
            } else {
                this.provider.dispatchArchivedAnnualReportsList(value);
            }
        }

        this.annualReportsFilter = null;
    }

    @Output() shareReportClicked = new EventEmitter<IAnnualReportTableList>();

    @Output() numberOfDocumentsPerPageChanged = new EventEmitter<number>();

    detachPager: boolean = true;
    local = localizationKeys;

    public columns: IColumn[];
    public errorMessage: Observable<string>;
    data: IAnnualReportTableList[];
    options: ITableOptions;
    viewState: AnnualReportListViewState = AnnualReportListViewState.ACTIVE;

    @Output() editButtonClicked: EventEmitter<string> = new EventEmitter();

    @Input() filterSubject?: BehaviorSubject<string>;

    constructor(private provider: AnnualReportProvider, private readonly localizedTexts: I18NextService) {}

    ngOnInit(): void {
        this.provider.removeActiveAnnualReport();
        this.provider
            .getAnnualReports$()
            .pipe(untilDestroyed(this))
            .subscribe((annualReports) => {
                this.handleGetAnnualReports(annualReports);
            });

        this.getMenuOptions();
    }

    private handleGetAnnualReports(annualReports: AnnualReportMetaInformationDTO[]): void {
        this.data = this.mapAnnualReportMetaInformationToListModel(annualReports);
    }

    private mapAnnualReportMetaInformationToListModel(
        annualReportsMetaInformation: AnnualReportMetaInformationDTO[],
    ): IAnnualReportTableList[] {
        const annualReportsTableList: IAnnualReportTableList[] = [];
        annualReportsMetaInformation.forEach((annualReportMetaInfo) => {
            const reportLanguage = this.mapLocaleToText(annualReportMetaInfo.locale);
            const annualReportsTableItem: IAnnualReportTableList = {
                _id: annualReportMetaInfo.annualReportEncryptedId,
                company: annualReportMetaInfo.companyName ? annualReportMetaInfo.companyName : '-',
                currentYearDate: annualReportMetaInfo.balanceSheetDate ? annualReportMetaInfo.balanceSheetDate : '-',
                previousYearDate: annualReportMetaInfo.balancePreviousYearDate
                    ? annualReportMetaInfo.balancePreviousYearDate
                    : '-',
                currency: annualReportMetaInfo.currency ? annualReportMetaInfo.currency.name : '-',
                createdAt: annualReportMetaInfo.createdAt,
                updatedAt: annualReportMetaInfo.updatedAt,
                disableSelection: annualReportMetaInfo.isPrintOnly,
                //   disableActions: annualReportMetaInfo.isPrintOnly,
                disableActions: true,
                locale: reportLanguage != null ? reportLanguage : '-',
                isSealed: annualReportMetaInfo.isSealed,
            };
            if (annualReportMetaInfo.companyName != null || annualReportMetaInfo.setupData?.company != null) {
                // We only add the item if it has a company name or setup data with company
                annualReportsTableList.push(annualReportsTableItem);
            }
        });

        return annualReportsTableList;
    }

    private mapLocaleToText(locale: string): string | null {
        return this.localizedTexts.t(LOCALE_LOCALIZATION_KEYS_MAP.get(locale?.toLocaleLowerCase())) as string;
    }

    onRowButtonClicked(annualReportMetaData: IAnnualReportTableList): void {
        this.provider.setActiveAnnualReportEncryptedId(annualReportMetaData._id);

        this.editButtonClicked.emit(annualReportMetaData._id);
    }

    onMenuItemClick(action: string) {
        if (
            !this.selectedIds?.length &&
            action !== AnnualReportListMenuActions.DISPLAY_ACTIVE &&
            action !== AnnualReportListMenuActions.DISPLAY_ARCHIVED
        ) {
            return;
        }

        switch (action) {
            case AnnualReportListMenuActions.DISPLAY_ACTIVE: {
                this.viewState = AnnualReportListViewState.ACTIVE;
                // We re-prepare fields because menu items change according to current displayed state

                this.provider.dispatchActiveAnnualReportsList(this.annualReportsFilter);
                break;
            }
            case AnnualReportListMenuActions.DISPLAY_ARCHIVED: {
                this.viewState = AnnualReportListViewState.ARCHIVED;
                // We re-prepare fields because menu items change according to current displayed state

                this.provider.dispatchArchivedAnnualReportsList(this.annualReportsFilter);
                break;
            }
            case AnnualReportListMenuActions.ARCHIVE: {
                this.provider.dispatchArchiveAnnualReports(this.selectedIds);
                break;
            }
            case AnnualReportListMenuActions.RESTORE: {
                this.provider.dispatchRestoreAnnualReports(this.selectedIds);
                break;
            }
            case AnnualReportListMenuActions.DELETE: {
                this.provider.dispatchDeleteAnnualReports(this.selectedIds);
                break;
            }
        }

        this.selectedIds = [];

        this.getMenuOptions();
        // debug('onMenuItemClick', action, selectedRows);
    }

    public getMenuOptions(): void {
        let menuOptions: IAumMenuItem[] = [];
        if (this.viewState === AnnualReportListViewState.ACTIVE) {
            menuOptions = [
                {
                    caption: this.localizedTexts.t(
                        this.local.aumico_annual_report_table_menu_option_display_archived,
                    ) as string,
                    action: AnnualReportListMenuActions.DISPLAY_ARCHIVED,
                },
                {
                    caption: this.localizedTexts.t(this.local.aumico_annual_report_table_menu_option_archive) as string,
                    action: AnnualReportListMenuActions.ARCHIVE,
                },
            ];
        } else if (this.viewState === AnnualReportListViewState.ARCHIVED) {
            menuOptions = [
                {
                    caption: this.localizedTexts.t(
                        this.local.aumico_annual_report_table_menu_option_display_active,
                    ) as string,
                    action: AnnualReportListMenuActions.DISPLAY_ACTIVE,
                },
                {
                    caption: this.localizedTexts.t(this.local.aumico_annual_report_table_menu_option_restore) as string,
                    action: AnnualReportListMenuActions.RESTORE,
                },
                {
                    caption: this.localizedTexts.t(this.local.aumico_annual_report_table_menu_option_delete) as string,
                    action: AnnualReportListMenuActions.DELETE,
                },
            ];
        }

        this.menuOptions = [...menuOptions];
    }

    public onPageSizeChanged(pageSize: number): void {
        this.numberOfDocumentsPerPageChanged.emit(pageSize);
    }

    onCheckBoxSelectionChanged(selectedRows: IAnnualReportTableList[]): void {
        this.selectedIds = selectedRows.map((selectedRow) => selectedRow._id);
    }
}
